import { format, parseISO } from 'date-fns';
import { AlertCircle, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  type AnalyticsFilters,
  type PromptAnalyticsItem,
  type PromptSortField,
  type SortOrder,
  usePromptsAnalytics,
} from '@/hooks/useAnalytics';
import { Loading } from '../shared';
import { SortableHeader } from './components/SortableHeader';

interface PromptsTableProps {
  filters: AnalyticsFilters;
}

const PAGE_SIZE = 10;

export const PromptsTable = ({ filters }: PromptsTableProps) => {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<PromptSortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [expandedPrompt, setExpandedPrompt] = useState<PromptAnalyticsItem | null>(null);

  const { data, isLoading, error } = usePromptsAnalytics(filters, page, PAGE_SIZE, sortBy, sortOrder);

  if (data && !data.feature_enabled) return null;

  const handleSort = (field: PromptSortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1); // Reset to first page on sort change
  };

  const totalPages = data ? Math.ceil(data.total_count / PAGE_SIZE) : 0;
  const prompts = data?.prompts ?? [];

  const formatDate = (isoString: string) => {
    try {
      const date = parseISO(isoString);
      return format(date, 'MMM d, yyyy');
    } catch {
      return isoString;
    }
  };

  if (error) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-center p-8 text-red-600">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>Failed to load prompts data</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
      <CardHeader>
        <CardTitle className="text-lg">User Prompts</CardTitle>
        <CardDescription>Detailed view of all user messages</CardDescription>
      </CardHeader>
      <CardContent>
      <div className="space-y-4">
        {/* Warning Banner */}
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> User prompts may contain confidential information. Handle with care.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : prompts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-500">
              <Eye className="h-5 w-5" />
            </div>
            <p className="text-sm font-medium text-slate-800">No prompts found</p>
            <p className="text-sm text-muted-foreground">Try adjusting your filters or date range</p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader
                      label="Date"
                      field="created_at"
                      currentField={sortBy}
                      currentOrder={sortOrder}
                      onSort={handleSort}
                      width="12%"
                    />
                    <SortableHeader
                      label="User"
                      field="user_name"
                      currentField={sortBy}
                      currentOrder={sortOrder}
                      onSort={handleSort}
                      width="22%"
                    />
                    <SortableHeader
                      label="Matter"
                      field="matter_name"
                      currentField={sortBy}
                      currentOrder={sortOrder}
                      onSort={handleSort}
                      width="20%"
                    />
                    <TableHead style={{ width: '46%' }}>Prompt</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prompts.map((prompt) => (
                    <TableRow key={prompt.message_id} className="group">
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(prompt.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">{prompt.user_name}</span>
                          <span className="text-sm text-muted-foreground">{prompt.user_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">{prompt.matter_name}</span>
                          {prompt.case_reference && (
                            <span className="text-sm text-muted-foreground">{prompt.case_reference}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground flex-1 line-clamp-2">
                            {prompt.content_preview}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Eye}
                            onClick={() => setExpandedPrompt(prompt)}
                            className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                            aria-label="View prompt details"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-2 flex items-center justify-between px-2">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={ChevronLeft}
                    aria-label="Previous page"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    icon={ChevronRight}
                    aria-label="Next page"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </CardContent>
    </Card>

      <Dialog open={!!expandedPrompt} onOpenChange={(open) => !open && setExpandedPrompt(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>User Prompt</DialogTitle>
          </DialogHeader>
          {expandedPrompt && (
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-4 rounded-md border border-border bg-slate-50 p-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">User</p>
                  <p className="text-sm text-foreground">{expandedPrompt.user_name}</p>
                  <p className="text-xs text-muted-foreground">{expandedPrompt.user_email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Matter</p>
                  <p className="text-sm text-foreground">{expandedPrompt.matter_name}</p>
                  {expandedPrompt.case_reference && (
                    <p className="text-xs text-muted-foreground">{expandedPrompt.case_reference}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Date</p>
                  <p className="text-sm text-foreground">
                    {formatDate(expandedPrompt.created_at)}
                  </p>
                </div>
                {expandedPrompt.mode && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Mode</p>
                    <p className="text-sm text-foreground capitalize">
                      {expandedPrompt.mode === 'agent' ? 'Reasoning' : expandedPrompt.mode}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-muted-foreground">Content</p>
                <div className="rounded-md border border-border bg-slate-50 p-4">
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {expandedPrompt.content}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
